/**
 * Homepage Content Data
 * 
 * Single source of truth for all Homepage content
 * No UI logic, only data structure
 * Used by: HomePage, CMS, Approval System
 */

export const homepageContent = {
  en: {
    languageLabel: 'Language',
    // CRITICAL: Update these to match CURRENT database values
    // These are used as fallback while API is loading
    // When API responds, these are replaced with database values
    heroTaglinePrefix: 'SCHOOL FOR',
    heroTaglineAccent: 'LIFE',  // Update this to match database whenever you change hero
    heroPhilosophy: 'FAITH, FORTITUDE AND FULFILMENT',
    quickLinksKicker: 'Quick Access',
    quickLinksTitle: 'Everything you need at a glance',
    applyNowTitle: 'Apply Now',
    applyNowText: 'Begin the admissions journey with a simple and guided first step.',
    schoolCalendarTitle: 'School Calendar',
    schoolCalendarText: 'View academic schedules, events, competitions, assemblies, cultural days, and field trips.',
    contactUsTitle: 'Contact Us',
    contactUsText: 'Connect with our team for enquiries, visits, or admissions support.',
    highlightsKicker: 'Highlights',
    highlightsTitle: 'Today on campus',
    highlightsIntro: 'Live updates and featured moments that reflect the rhythm of the school day.',
    highlightsLiveLabel: 'Live update',
    highlightsNote: 'Auto-updates based on the day and rotates every few seconds.',
    // CRITICAL: No fallback items - if CMS data fails, section won't render
    // This is intentional to prevent showing stale/loading data
    highlightsItems: [],
    newsCountdownKicker: 'What Is Next',
    newsCountdownTitle: 'News and opening day countdown',
    newsCountdownIntro: 'Stay updated with the latest school stories while we count down to opening day.',
    latestNewsLabel: 'Latest News',
    newsItems: [
      {
        date: 'April 12, 2026',
        title: 'Parent orientation week begins',
        summary: 'Families are invited to school tours, programme briefings, and classroom introductions.',
        fullContent: `
          <p>We are excited to welcome all families to our comprehensive Parent Orientation Week, designed to help you understand our educational philosophy, meet our dedicated staff, and explore our state-of-the-art facilities.</p>
          
          <p>Throughout the week, parents will have the opportunity to participate in guided school tours, attend detailed programme briefings covering our curriculum approach, and visit classrooms where their children will learn and grow. Our experienced teachers and administrators will be available to answer questions and provide insights into daily school life.</p>
          
          <p>This orientation is an essential first step in building a strong partnership between home and school. We believe that when families and educators work together, students thrive academically, socially, and emotionally.</p>
          
          <p>Sessions will be held daily from 9:00 AM to 3:00 PM. Light refreshments will be provided. We look forward to meeting you and beginning this exciting educational journey together.</p>
        `,
      },
      {
        date: 'April 18, 2026',
        title: 'Student talent showcase',
        summary: 'A celebration of music, performance, and student creativity across grade levels.',
        fullContent: `
          <p>Join us for an inspiring evening as our students take center stage to showcase their remarkable talents in music, drama, dance, and visual arts. This annual event celebrates the diverse creative abilities of our student body and demonstrates the fruits of their dedication and practice.</p>
          
          <p>The showcase will feature performances from students across all grade levels, including solo musical performances, ensemble pieces, dramatic readings, dance choreography, and displays of visual artwork. Each performance reflects not only individual talent but also the collaborative spirit that defines our school community.</p>
          
          <p>Our arts programme is designed to nurture creativity, build confidence, and provide students with opportunities to express themselves in meaningful ways. Through regular practice, constructive feedback, and supportive encouragement, students develop skills that extend far beyond the stage or canvas.</p>
          
          <p>The event will be held in our main auditorium at 6:00 PM. Admission is free, and all family members are welcome. Light refreshments will be available during intermission.</p>
        `,
      },
      {
        date: 'April 24, 2026',
        title: 'Community service collaboration',
        summary: 'Students join outreach initiatives that strengthen empathy and social responsibility.',
        fullContent: `
          <p>Our students will participate in meaningful community service projects designed to foster empathy, social responsibility, and a deeper understanding of the world beyond our campus. These initiatives are a core component of our character education programme.</p>
          
          <p>This month's collaboration includes partnerships with local charities, environmental conservation efforts, and support for elderly community members. Students will work in small groups, guided by teachers and community leaders, to make a tangible positive impact.</p>
          
          <p>Through hands-on service experiences, students learn the value of compassion, teamwork, and civic engagement. They develop a sense of agency and understand that even small actions can create meaningful change. These lessons complement our academic curriculum and help shape well-rounded, socially conscious individuals.</p>
          
          <p>Parents are encouraged to support their children's participation and discuss their experiences at home. Together, we are building a generation of thoughtful, engaged citizens who understand their responsibility to contribute to the common good.</p>
        `,
      },
    ],
    countdownLabel: 'Opening Day Countdown',
    countdownTitle: 'School Opening Day',
    countdownDateText: 'Monday, July 13, 2026',
    countdownDescription: 'We are preparing every detail to welcome students and families with excellence.',
    countdownDays: 'Days',
    countdownHours: 'Hours',
    countdownMinutes: 'Minutes',
    countdownSeconds: 'Seconds',
    countdownComplete: 'Opening day has arrived. Welcome to the new school year.',
  },
  id: {
    languageLabel: 'Bahasa',
    heroTaglinePrefix: 'SEKOLAH UNTUK',
    heroTaglineAccent: 'HIDUP',
    heroPhilosophy: 'IMAN, KETEGUHAN, DAN PENCAPAIAN',
    quickLinksKicker: 'Akses Cepat',
    quickLinksTitle: 'Semua informasi penting dalam satu tempat',
    applyNowTitle: 'Daftar Sekarang',
    applyNowText: 'Mulai proses pendaftaran dengan langkah awal yang mudah dan terarah.',
    schoolCalendarTitle: 'Kalender Sekolah',
    schoolCalendarText: 'Lihat jadwal akademik, acara, kompetisi, upacara, hari budaya, dan karyawisata.',
    contactUsTitle: 'Hubungi Kami',
    contactUsText: 'Hubungi tim kami untuk pertanyaan, kunjungan, atau bantuan pendaftaran.',
    highlightsKicker: 'Sorotan',
    highlightsTitle: 'Aktivitas hari ini di sekolah',
    highlightsIntro: 'Update langsung dan momen utama yang mengikuti ritme kegiatan sekolah.',
    highlightsLiveLabel: 'Update langsung',
    highlightsNote: 'Memperbarui isi sesuai hari dan berganti otomatis setiap beberapa detik.',
    highlightsItems: [
      {
        title: 'Apel pagi dan kepemimpinan siswa',
        summary: 'Awal hari yang tenang dengan pengumuman, nilai karakter, dan fokus bersama.',
      },
      {
        title: 'Fokus akademik dan keterlibatan kelas',
        summary: 'Guru memandu pembelajaran terarah, sesi pendampingan, dan diskusi aktif di kelas.',
      },
      {
        title: 'Klub, kreativitas, dan kolaborasi',
        summary: 'Siswa mengeksplorasi minat melalui klub, proyek, dan kerja tim secara langsung.',
      },
      {
        title: 'Seni, budaya, dan semangat kebersamaan',
        summary: 'Ruang untuk cultural day, penampilan, dan ekspresi diri yang membangun percaya diri.',
      },
      {
        title: 'Refleksi, chapel, dan kepedulian komunitas sekolah',
        summary: 'Akhir pekan sekolah ditutup dengan rasa syukur, refleksi, dan perhatian bagi keluarga besar sekolah.',
      },
    ],
    newsCountdownKicker: 'Selanjutnya',
    newsCountdownTitle: 'Berita dan hitung mundur hari pembukaan',
    newsCountdownIntro: 'Ikuti kabar terbaru sekolah sambil menghitung mundur menuju hari pembukaan.',
    latestNewsLabel: 'Berita Terkini',
    newsItems: [
      {
        date: '12 April 2026',
        title: 'Minggu orientasi orang tua dimulai',
        summary: 'Keluarga diundang untuk tur sekolah, penjelasan program, dan pengenalan kelas.',
        fullContent: `
          <p>Kami sangat senang menyambut semua keluarga dalam Minggu Orientasi Orang Tua yang komprehensif, dirancang untuk membantu Anda memahami filosofi pendidikan kami, bertemu dengan staf kami yang berdedikasi, dan menjelajahi fasilitas kami yang canggih.</p>
          
          <p>Sepanjang minggu, orang tua akan memiliki kesempatan untuk berpartisipasi dalam tur sekolah terpandu, menghadiri penjelasan program terperinci yang mencakup pendekatan kurikulum kami, dan mengunjungi ruang kelas di mana anak-anak mereka akan belajar dan berkembang. Guru dan administrator berpengalaman kami akan tersedia untuk menjawab pertanyaan dan memberikan wawasan tentang kehidupan sekolah sehari-hari.</p>
          
          <p>Orientasi ini adalah langkah pertama yang penting dalam membangun kemitraan yang kuat antara rumah dan sekolah. Kami percaya bahwa ketika keluarga dan pendidik bekerja sama, siswa berkembang secara akademis, sosial, dan emosional.</p>
          
          <p>Sesi akan diadakan setiap hari dari pukul 09:00 hingga 15:00. Makanan ringan akan disediakan. Kami menantikan untuk bertemu Anda dan memulai perjalanan pendidikan yang menarik ini bersama-sama.</p>
        `,
      },
      {
        date: '18 April 2026',
        title: 'Pentas bakat siswa',
        summary: 'Perayaan musik, penampilan, dan kreativitas siswa dari berbagai jenjang.',
        fullContent: `
          <p>Bergabunglah dengan kami untuk malam yang menginspirasi saat siswa kami tampil di panggung untuk menunjukkan bakat luar biasa mereka dalam musik, drama, tari, dan seni visual. Acara tahunan ini merayakan kemampuan kreatif yang beragam dari siswa kami dan menunjukkan hasil dari dedikasi dan latihan mereka.</p>
          
          <p>Pertunjukan akan menampilkan penampilan dari siswa di semua tingkat kelas, termasuk pertunjukan musik solo, karya ensemble, pembacaan dramatis, koreografi tari, dan pameran karya seni visual. Setiap pertunjukan mencerminkan tidak hanya bakat individu tetapi juga semangat kolaboratif yang mendefinisikan komunitas sekolah kami.</p>
          
          <p>Program seni kami dirancang untuk memelihara kreativitas, membangun kepercayaan diri, dan memberikan siswa kesempatan untuk mengekspresikan diri mereka dengan cara yang bermakna. Melalui latihan rutin, umpan balik konstruktif, dan dorongan yang mendukung, siswa mengembangkan keterampilan yang jauh melampaui panggung atau kanvas.</p>
          
          <p>Acara akan diadakan di auditorium utama kami pada pukul 18:00. Masuk gratis, dan semua anggota keluarga dipersilakan. Makanan ringan akan tersedia selama istirahat.</p>
        `,
      },
      {
        date: '24 April 2026',
        title: 'Kolaborasi layanan masyarakat',
        summary: 'Siswa terlibat dalam inisiatif sosial yang menumbuhkan empati dan tanggung jawab.',
        fullContent: `
          <p>Siswa kami akan berpartisipasi dalam proyek layanan masyarakat yang bermakna yang dirancang untuk menumbuhkan empati, tanggung jawab sosial, dan pemahaman yang lebih dalam tentang dunia di luar kampus kami. Inisiatif ini adalah komponen inti dari program pendidikan karakter kami.</p>
          
          <p>Kolaborasi bulan ini mencakup kemitraan dengan badan amal lokal, upaya konservasi lingkungan, dan dukungan untuk anggota masyarakat lanjut usia. Siswa akan bekerja dalam kelompok kecil, dipandu oleh guru dan pemimpin masyarakat, untuk membuat dampak positif yang nyata.</p>
          
          <p>Melalui pengalaman layanan langsung, siswa belajar nilai kasih sayang, kerja tim, dan keterlibatan sipil. Mereka mengembangkan rasa agensi dan memahami bahwa bahkan tindakan kecil dapat menciptakan perubahan yang berarti. Pelajaran ini melengkapi kurikulum akademis kami dan membantu membentuk individu yang berimbang dan sadar sosial.</p>
          
          <p>Orang tua didorong untuk mendukung partisipasi anak-anak mereka dan mendiskusikan pengalaman mereka di rumah. Bersama-sama, kami membangun generasi warga negara yang bijaksana dan terlibat yang memahami tanggung jawab mereka untuk berkontribusi pada kebaikan bersama.</p>
        `,
      },
    ],
    countdownLabel: 'Hitung Mundur Pembukaan',
    countdownTitle: 'Hari Pembukaan Sekolah',
    countdownDateText: 'Senin, 13 Juli 2026',
    countdownDescription: 'Kami menyiapkan setiap detail untuk menyambut siswa dan keluarga dengan maksimal.',
    countdownDays: 'Hari',
    countdownHours: 'Jam',
    countdownMinutes: 'Menit',
    countdownSeconds: 'Detik',
    countdownComplete: 'Hari pembukaan telah tiba. Selamat datang di tahun ajaran baru.',
  },
};

export default homepageContent;
